import { useQuery } from "@tanstack/react-query";
import { learningService } from "../../../api/learningService";
import { getErrorMessage } from "../../../utils/apiError";
import { formatManilaDate } from "../../../utils/manilaTime";
import { cx } from "../../../utils/classNames";
import {
  formatLearningHours,
  formatSeminarWhen,
  LEARNING_KEY,
  SEMINAR_TYPE_LABELS,
} from "../learningUtils";
import { SkillChips } from "./SeminarCard";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Learning.module.css";

// Skills register (skills earned from attended seminars) + attended seminars.
const LearningRecord = ({ onOpenSeminar }) => {
  const query = useQuery({
    queryKey: [LEARNING_KEY, "record"],
    queryFn: learningService.getMyRecord,
  });

  if (query.isLoading) {
    return (
      <div className={cx(ui.card, ui.empty)}>
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Loading your learning record…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        {getErrorMessage(query.error, "Unable to load your learning record.")}
      </div>
    );
  }

  const { totals, skills, attended } = query.data;
  const totalItems = [
    { label: "Attended", value: totals.attended },
    { label: "Training hours", value: formatLearningHours(totals.hours) },
    { label: "Skills", value: skills.length },
    { label: "Upcoming", value: totals.upcoming },
    { label: "Absent", value: totals.absent },
    { label: "Excused", value: totals.excused },
  ];

  return (
    <>
      <div className={styles.totals}>
        {totalItems.map((item) => (
          <div key={item.label} className={styles.totalCard}>
            <div className={styles.totalLabel}>{item.label}</div>
            <div className={styles.totalValue}>{item.value}</div>
          </div>
        ))}
      </div>

      <div className={ui.card}>
        <div className={ui.cardHeader}>
          <span className={ui.cardTitle}>Skills register</span>
          <span className={ui.hint}>Earned by attending seminars</span>
        </div>
        <div className={ui.tableWrapper}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Skill</th>
                <th>Category</th>
                <th>Seminars</th>
                <th>Hours</th>
                <th>First attended</th>
                <th>Last attended</th>
              </tr>
            </thead>
            <tbody>
              {skills.length === 0 && (
                <tr>
                  <td colSpan={6} className={cx(ui.muted, "text-center")}>
                    No skills yet. Skills are added when HR marks you as
                    attended.
                  </td>
                </tr>
              )}
              {skills.map((skill) => (
                <tr key={skill.id ?? skill.name}>
                  <td className={ui.strong}>{skill.name}</td>
                  <td>{skill.category || "—"}</td>
                  <td>{skill.seminars}</td>
                  <td>{formatLearningHours(skill.hours)}</td>
                  <td className={ui.noWrap}>
                    {formatManilaDate(skill.firstAttendedAt)}
                  </td>
                  <td className={ui.noWrap}>
                    {formatManilaDate(skill.lastAttendedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={ui.card}>
        <div className={ui.cardHeader}>
          <span className={ui.cardTitle}>Seminars attended</span>
        </div>
        <div className={ui.tableWrapper}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Seminar</th>
                <th>When</th>
                <th>Type</th>
                <th>Hours</th>
                <th>Skills</th>
              </tr>
            </thead>
            <tbody>
              {attended.length === 0 && (
                <tr>
                  <td colSpan={5} className={cx(ui.muted, "text-center")}>
                    No attended seminars yet.
                  </td>
                </tr>
              )}
              {attended.map((item) => (
                <tr
                  key={item.seminarId}
                  className={ui.clickableRow}
                  onClick={() => onOpenSeminar({ id: item.seminarId, ...item })}
                >
                  <td className={ui.strong}>{item.title}</td>
                  <td className={cx(ui.noWrap, ui.muted)}>
                    {formatSeminarWhen(item.startAt, item.endAt)}
                  </td>
                  <td>{SEMINAR_TYPE_LABELS[item.type] ?? item.type}</td>
                  <td>{formatLearningHours(item.hours)}</td>
                  <td>
                    <SkillChips skills={item.skills} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default LearningRecord;
